import { useContext } from 'react'
import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons'

import {
  Box,
  Button,
  DashboardModule,
  Form,
  ModalContext,
  SectionDivider,
  SidebarModal,
  Text,
} from '@domain-group/suit-theme'

import { HandleCallbackActionParams, digObject } from '@domain-group/javascript-utils'

import useOrderBundleItems from '@hooks/useOrderBundleItems'
import useAgencyOrderBundle, { useRelations } from '@hooks/useAgencyOrderBundle'

import { AgencyOrderBundleModel } from '@models/types'

import OrderBundleItemListItem from './components/OrderBundleItemListItem'

const MODAL_KEY = 'ManageAgencyOrderBundleModal'

interface ModalCallbacks {
  closeModal: () => void,
  updateAgencyOrderBundle: (toggleVisibilityPayload: HandleCallbackActionParams) => void,
}

type ConfirmToggleVisibilityParams = {
  agencyOrderBundle: AgencyOrderBundleModel,
  callbacks: ModalCallbacks,
  toggleFn: () => void,
}

const confirmToggleVisibility = (params: ConfirmToggleVisibilityParams) => {
  const {
    agencyOrderBundle, agencyOrderBundle: { hidden }, callbacks, toggleFn,
  } = params
  const { closeModal, updateAgencyOrderBundle } = callbacks

  const toggleVisibilityPayload = {
    callbacks: {
      action: toggleFn,
      afterAction: closeModal,
    },
    entityParams: agencyOrderBundle,
    toastText: 'Bundle Updated',
  }

  swal
    .fire({
      confirmButtonColor: '#DD6B55',
      confirmButtonText: `Yes, ${hidden ? 'show' : 'hide'} it.`,
      icon: 'warning',
      showCancelButton: true,
      title: `${hidden ? 'Show' : 'Hide'} Bundle?`,
    })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        updateAgencyOrderBundle(toggleVisibilityPayload)
      }
    })
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageAgencyOrderBundleModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { agencyOrderBundle: initAgencyOrderBundle } = modalPayload

  const agencyOrderBundlePayload = useAgencyOrderBundle(initAgencyOrderBundle)
  const {
    agencyOrderBundle,
    agencyOrderBundle: {
      order_bundle_id,
    },
    callbacks: {
      toggleVisibility: toggleFn,
    },
  } = agencyOrderBundlePayload

  const orderBundleItemsPayload = useOrderBundleItems({
    filters: {
      order_bundle_id: agencyOrderBundle.order_bundle_id,
    },
    performHttpRequests: !!agencyOrderBundle?.id,
    requestOptions: {
      include_order_bundle_item_publication: true,
    },
  })

  const { orderBundle: { title: orderBundleTitle } } = useRelations(agencyOrderBundle)

  const {
    hasFilteredOrderBundleItems,
    filteredOrderBundleItems,
  } = orderBundleItemsPayload

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal} size="large">
      <SidebarModal.Header callbacks={callbacks} title="Manage" titleSecondLine="Bundle" />

      <SidebarModal.SubHeader>
        <Box flexDirection="column">
          <Text>{orderBundleTitle}</Text>

          <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">
            # {order_bundle_id}
          </Text>
        </Box>
      </SidebarModal.SubHeader>

      <SidebarModal.Content>
        {hasFilteredOrderBundleItems && (
          <>
            <SectionDivider>ITEMS</SectionDivider>

            {/*
             // @ts-expect-error TS2322 */}
            <DashboardModule.ScrollableSection maxHeight={600}>
              {filteredOrderBundleItems.map(item => <OrderBundleItemListItem key={item.id} orderBundleItem={item} />)}
            </DashboardModule.ScrollableSection>
          </>
        )}

        <Form.Field label="More Options" marginTop="large">
          <Button
            buttonStyle="secondaryUtility"
            icon={<FontAwesomeIcon icon={agencyOrderBundle.hidden ? faEye : faEyeSlash} />}
            onClick={() => confirmToggleVisibility({
              agencyOrderBundle, callbacks, toggleFn,
            })}
            size="medium"
          >
            {`${agencyOrderBundle.hidden ? 'Show' : 'Hide'} Bundle`}
          </Button>
        </Form.Field>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageAgencyOrderBundleModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
